import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MCPServerCard, type MCPServer } from '../components/MCPServerCard';
import { MCPServerForm } from '../components/MCPServerForm';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { createApiClient } from '@/lib/api-client';
import { useApiStore } from '@/store/api';

interface MCPServerFormData {
  name: string;
  url: string;
  oauth_enabled: boolean;
}

/**
 * MCP Servers management page
 * - List all configured MCP servers
 * - Add/Edit/Delete servers
 * - Toggle OAuth
 * - Copy MCP connection links
 * - View connection status
 */
export function MCPServersPage() {
  const navigate = useNavigate();
  const { apiUrl } = useApiStore();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);

  const loadServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.GET('/v1/mcp-servers');

      if (apiError || !data) {
        throw new Error('Failed to load MCP servers');
      }

      setServers(
        (data.mcp_servers || []).map((s) => ({
          ...s,
          assigned_agents: 0,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MCP servers');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleAddServer = async (data: MCPServerFormData) => {
    try {
      setError(null);
      const client = createApiClient(apiUrl);
      const { data: result, error: apiError } = await client.POST('/v1/mcp-servers', {
        body: {
          name: data.name,
          url: data.url,
          oauth_enabled: data.oauth_enabled,
        },
      });

      if (apiError || !result) {
        throw new Error('Failed to create MCP server');
      }

      setShowForm(false);
      await loadServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create MCP server');
    }
  };

  const handleEditServer = async (data: MCPServerFormData) => {
    if (!editingServer) return;

    try {
      setError(null);
      const client = createApiClient(apiUrl);
      const { data: result, error: apiError } = await client.PUT('/v1/mcp-servers/{server_id}', {
        params: { path: { server_id: editingServer.id } },
        body: {
          name: data.name,
          url: data.url,
          oauth_enabled: data.oauth_enabled,
        },
      });

      if (apiError || !result) {
        throw new Error('Failed to update MCP server');
      }

      setEditingServer(null);
      await loadServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update MCP server');
    }
  };

  const handleDeleteServer = async (serverId: string) => {
    try {
      setError(null);
      const client = createApiClient(apiUrl);
      const { error: apiError } = await client.DELETE('/v1/mcp-servers/{server_id}', {
        params: { path: { server_id: serverId } },
      });

      if (apiError) {
        throw new Error('Failed to delete MCP server');
      }

      await loadServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete MCP server');
    }
  };

  const [copiedServerId, setCopiedServerId] = useState<string | null>(null);

  const handleCopyLink = (serverId: string) => {
    const server = servers.find((s) => s.id === serverId);
    if (!server) return;

    const mcpLink = `mcp://${server.url.replace(/^https?:\/\//, '')}?token=${server.endpoint_token}`;

    navigator.clipboard.writeText(mcpLink).then(() => {
      setCopiedServerId(serverId);
      setTimeout(() => setCopiedServerId(null), 2000);
    });
  };

  const handleToggleOAuth = async (serverId: string, enabled: boolean) => {
    try {
      setError(null);
      const client = createApiClient(apiUrl);
      const { error: apiError } = await client.PUT('/v1/mcp-servers/{server_id}', {
        params: { path: { server_id: serverId } },
        body: { oauth_enabled: enabled },
      });

      if (apiError) {
        throw new Error('Failed to update OAuth setting');
      }

      await loadServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle OAuth');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-slate-300 hover:text-white transition-colors text-sm sm:text-base"
                data-testid="back-button"
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-white">MCP Servers</h1>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              variant="primary"
              className="min-h-[44px] touch-manipulation"
              data-testid="add-server-button"
            >
              + Add Server
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {(showForm || editingServer) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-white mb-4">
                {editingServer ? 'Edit Server' : 'Add MCP Server'}
              </h2>
              <MCPServerForm
                initialData={
                  editingServer
                    ? {
                        name: editingServer.name,
                        url: editingServer.url,
                        oauth_enabled: editingServer.oauth_enabled,
                      }
                    : undefined
                }
                onSubmit={editingServer ? handleEditServer : handleAddServer}
                onCancel={() => {
                  setShowForm(false);
                  setEditingServer(null);
                }}
                isEditing={!!editingServer}
              />
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="text-slate-400">Loading MCP servers...</div>
          </div>
        ) : servers.length === 0 ? (
          <EmptyState
            icon="&#x1F50C;"
            title="No MCP Servers"
            description="Add your first MCP server to enable agent integrations"
            action={
              <Button
                onClick={() => setShowForm(true)}
                variant="primary"
                size="lg"
                className="min-h-[44px] touch-manipulation"
              >
                Add MCP Server
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4">
            {servers.map((server) => (
              <MCPServerCard
                key={server.id}
                server={server}
                onEdit={setEditingServer}
                onDelete={handleDeleteServer}
                onCopyLink={handleCopyLink}
                onToggleOAuth={handleToggleOAuth}
              />
            ))}
          </div>
        )}

        {/* Copy Success Toast */}
        {copiedServerId && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg text-sm font-medium animate-fade-in">
            MCP link copied to clipboard
          </div>
        )}

        {/* Info Box */}
        {servers.length > 0 && (
          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-400 mb-2">About MCP Servers</h3>
            <p className="text-sm text-slate-300">
              MCP (Model Context Protocol) servers enable your agents to access external tools and
              data sources. Configure servers here and assign them to agents in the Agents page.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
